import {Box, Stack, Typography} from "@mui/material";
import type {ContentMetadata} from "../../../../../sdo/ContentMetadata.ts";
import type {ODIPointer} from "../../../../../sdo/ContentFramePointer.ts";

interface ContentMetadataCardProps {
    metadata: ContentMetadata;
}

function PointerPill({pointer}: { pointer: ODIPointer }) {
    return (
        <Box sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            padding: "2px 8px",
            borderRadius: "10px",
            border: "1px solid rgba(112, 147, 160, 0.35)",
            backgroundColor: "rgba(26, 37, 48, 0.85)",
        }}>
            <img
                src={`/images/intel/${pointer.typeKey}.svg`}
                width={14}
                height={14}
                alt=""
                aria-hidden="true"
                onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
            />
            <Typography sx={{color: "#ffffff", fontSize: "0.70rem", textAlign: "left"}}>
                {pointer.display}
            </Typography>
        </Box>
    );
}

function ContentMetadataCard({metadata}: ContentMetadataCardProps) {
    const hasGroup = metadata.groupKey && metadata.groupKey.trim().length > 0;
    const hasValue = metadata.value && metadata.value.trim().length > 0;
    const hasPointers = metadata.pointers && metadata.pointers.length > 0;

    return (
        <Box sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "stretch",
            width: "100%",
            background: "linear-gradient(155deg, rgba(0, 71, 106, 0.30) 0%, rgba(0, 38, 53, 0.30) 25%, rgba(0, 0, 0, 0.30) 90%, rgba(0, 0, 0, 0.40) 100%)",
            borderRadius: "12px",
            border: "0.5px solid #3F464F",
            overflow: "hidden",
        }}>
            {/* Left: metadata icon */}
            <Box sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "8px 10px",
            }}>
                <img src="/images/metadata.svg" width={24} height={24} alt="Metadata" style={{opacity: 0.85}}/>
            </Box>

            {/* Center: header + value + pointers */}
            <Box sx={{flex: 1, minWidth: 0, display: "flex", flexDirection: "column", padding: "6px 10px 8px 4px"}}>
                {/* Header row: key (left) + groupKey (right, dimmed) */}
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{width: "100%"}}>
                    <Typography sx={{
                        color: "#f2f5fa",
                        fontWeight: 700,
                        fontSize: "0.85rem",
                        textAlign: "left",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}>
                        {metadata.key}
                    </Typography>
                    {hasGroup && (
                        <Typography sx={{
                            color: "rgba(242, 245, 250, 0.55)",
                            fontSize: "0.75rem",
                            fontWeight: 500,
                            textAlign: "right",
                            whiteSpace: "nowrap",
                        }}>
                            {metadata.groupKey}
                        </Typography>
                    )}
                </Stack>

                {/* Value */}
                {hasValue && (
                    <Typography sx={{
                        color: "rgba(242, 245, 250, 0.85)",
                        fontSize: "0.75rem",
                        fontWeight: 300,
                        lineHeight: 1.5,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        textAlign: "left",
                        marginTop: "4px",
                    }}>
                        {metadata.value}
                    </Typography>
                )}

                {/* Pointer pills */}
                {hasPointers && (
                    <Box sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "6px",
                        marginTop: "6px",
                    }}>
                        {metadata.pointers.map((p) => (
                            <PointerPill key={p.uid} pointer={p}/>
                        ))}
                    </Box>
                )}
            </Box>
        </Box>
    );
}

export default ContentMetadataCard;
